import { useState } from "react";

import { AdminModal } from "@/components/admin/shared/AdminModal";
import { CancelButton } from "@/components/admin/shared/PageHeader";
import {
  PennButton,
  PennField,
  PennInput,
  PennText,
  PennTextarea,
} from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import {
  useFinalizeQuote,
  useRespondAndCloseTask,
  useTask,
  useTaskApplications,
  useTaskSnapshot,
  useTaskStateHistory,
} from "@/hooks/useTasks";
import { AttachmentsPanel } from "@/components/attachments/AttachmentsPanel";
import {
  useAddManualLineItem,
  useDeleteManualLineItem,
  useManualLineItems,
} from "@/hooks/useManualLineItems";
import {
  useDownloadCostAnalysis,
  useDownloadRoiReport,
  useDownloadSupportResponse,
  useDownloadQuote,
} from "@/hooks/useQuoteDocuments";
import {
  useApprovalState,
  useDecideApproval,
  useReassignApproval,
} from "@/hooks/useApprovals";
import type { DocumentFormat } from "@/api/quoteDocuments";
import type { TaskDetail } from "@/api/tasks";

type Props = {
  taskId: string;
  onClose: () => void;
};

function apiErrorMessage(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message ?? fallback
  );
}

export const TaskDetailModal = ({ taskId, onClose }: Props) => {
  const role = useAppSelector((s) => s.auth.user?.role ?? null);
  const canSeePrices = !!role?.canSeePrices;
  const canGenerateQuote = !!role?.canGenerateQuote;
  const canGenerateRoi = !!role?.canGenerateRoi;
  const canApprove = !!role?.canApproveEngineering;
  const canConfigure = !!role?.canCreateApplications;

  const { data: task } = useTask(taskId);
  const isMessageOnlySupport =
    !!task && !task.hasSnapshot && !!task.comment?.trim();
  const hasQuotationPayload = !!task && !isMessageOnlySupport;
  const { data: snapshot } = useTaskSnapshot(taskId, hasQuotationPayload);
  const { data: taskApplications } = useTaskApplications(
    taskId,
    hasQuotationPayload && canConfigure,
  );
  const { data: history } = useTaskStateHistory(
    taskId,
    { limit: 50 },
    hasQuotationPayload,
  );
  const { data: lineItems } = useManualLineItems(
    taskId,
    hasQuotationPayload && canGenerateQuote,
  );
  const { data: approval } = useApprovalState(
    taskId,
    hasQuotationPayload && canApprove,
  );

  const currency = snapshot?.selectedOptions?.[0]?.currency ?? "EUR";
  const money = (v: string | number | null | undefined): string => {
    if (!canSeePrices) return "—";
    if (v == null || v === "") return "—";
    return `${currency} ${v}`;
  };

  const [docError, setDocError] = useState<string | null>(null);
  const [supportResponse, setSupportResponse] = useState("");
  const [supportError, setSupportError] = useState<string | null>(null);

  // --- Documents ------------------------------------------------------------
  const costAnalysis = useDownloadCostAnalysis();
  const roiReport = useDownloadRoiReport();
  const downloadQuote = useDownloadQuote();
  const respondAndClose = useRespondAndCloseTask();
  const supportDocument = useDownloadSupportResponse();
  const finalize = useFinalizeQuote();

  const handleCostAnalysis = () => {
    setDocError(null);
    costAnalysis.mutate(
      { taskId },
      {
        onError: (e) =>
          setDocError(apiErrorMessage(e, "Could not generate cost analysis.")),
      },
    );
  };
  const handleRoi = () => {
    setDocError(null);
    roiReport.mutate(
      { taskId, body: { baselineMachineType: null } },
      {
        onError: (e) =>
          setDocError(apiErrorMessage(e, "Could not generate ROI report.")),
      },
    );
  };

  // The quotation streams straight to a download (not persisted, LD-3), and
  // (decision #2) a successful download auto-finalizes the task.
  const handleRenderQuote = (format: DocumentFormat) => {
    setDocError(null);
    downloadQuote.mutate(
      { taskId, body: { format } },
      {
        onSuccess: () => {
          if (!task?.quotedAt) finalize.mutate({ taskId });
        },
        onError: (e) =>
          setDocError(
            apiErrorMessage(
              e,
              "Could not generate the quotation. The approval workflow may be incomplete.",
            ),
          ),
      },
    );
  };

  const docBusy =
    costAnalysis.isPending ||
    roiReport.isPending ||
    downloadQuote.isPending ||
    supportDocument.isPending ||
    finalize.isPending;

  const handleRespondAndClose = () => {
    const response = supportResponse.trim();
    setSupportError(null);
    if (!response) {
      setSupportError("Enter response comments before closing this task.");
      return;
    }
    respondAndClose.mutate(
      { taskId, response },
      {
        onSuccess: () => setSupportResponse(""),
        onError: (e) =>
          setSupportError(
            apiErrorMessage(e, "Could not respond and close this task."),
          ),
      },
    );
  };

  const handleSupportDownload = (format: DocumentFormat) => {
    setSupportError(null);
    supportDocument.mutate(
      { taskId, format },
      {
        onError: (e) =>
          setSupportError(
            apiErrorMessage(
              e,
              `Could not download the ${format.toUpperCase()} response.`,
            ),
          ),
      },
    );
  };

  // --- Manual line items ----------------------------------------------------
  const addLine = useAddManualLineItem();
  const deleteLine = useDeleteManualLineItem();
  const [lineForm, setLineForm] = useState({
    description: "",
    price: "",
    qty: "1",
  });
  const [lineError, setLineError] = useState<string | null>(null);

  const handleAddLine = () => {
    setLineError(null);
    const qty = Number(lineForm.qty);
    if (
      !lineForm.description.trim() ||
      !lineForm.price.trim() ||
      !Number.isFinite(qty)
    ) {
      setLineError("Description, price and quantity are required.");
      return;
    }
    addLine.mutate(
      {
        taskId,
        body: {
          description: lineForm.description.trim(),
          price: lineForm.price.trim(),
          qty: Math.max(0, Math.floor(qty)),
          currency,
        },
      },
      {
        onSuccess: () => setLineForm({ description: "", price: "", qty: "1" }),
        onError: (e) =>
          setLineError(apiErrorMessage(e, "Could not add the line item.")),
      },
    );
  };

  // --- Approvals ------------------------------------------------------------
  const decide = useDecideApproval();
  const reassign = useReassignApproval();
  const [decisionComment, setDecisionComment] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const current = approval?.currentAssignment ?? null;
  const canDecide =
    canApprove && !!current && !approval?.complete && !approval?.rejected;

  const handleDecide = (decision: "approve" | "reject") => {
    setApprovalError(null);
    decide.mutate(
      { taskId, body: { decision, comment: decisionComment.trim() || null } },
      {
        onSuccess: () => setDecisionComment(""),
        onError: (e) =>
          setApprovalError(
            apiErrorMessage(e, "Could not record the decision."),
          ),
      },
    );
  };
  const handleReassign = () => {
    setApprovalError(null);
    if (!reassignTo.trim()) {
      setApprovalError("Enter the new assignee user id.");
      return;
    }
    reassign.mutate(
      {
        taskId,
        body: {
          newAssigneeUserId: reassignTo.trim(),
          comment: decisionComment.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setReassignTo("");
          setDecisionComment("");
        },
        onError: (e) =>
          setApprovalError(apiErrorMessage(e, "Could not reassign.")),
      },
    );
  };

  // CR-005: a support response stamps the task Quoted (no bespoke "closed"
  // status), so "already responded" is derived from quotedAt — the same signal
  // that flips a normal task to QUOTED.
  const isResponded = task?.quotedAt != null;
  const statusLabel = isResponded ? "QUOTED" : "OPEN";

  return (
    <AdminModal
      open
      onClose={onClose}
      title={
        isMessageOnlySupport
          ? "Respond and Close"
          : `Task ${taskId.slice(0, 8)}…`
      }
      maxWidth="max-w-4xl"
      footer={<CancelButton onClick={onClose} />}
    >
      <div className="space-y-6">
        {/* Meta */}
        <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Status">
            <span
              className={`px-2 py-0.5 text-small-medium rounded border ${
                isResponded
                  ? "bg-penn-100 text-penn-700 border-penn-400"
                  : "bg-green-100 text-green-700 border-green-300"
              }`}
            >
              {statusLabel}
            </span>
          </Field>
          <Field label="Flow">{task?.flowType ?? "—"}</Field>
          <Field label="Updated">{fmtDate(task?.updatedAt)}</Field>
          <Field label="Requested by">{requesterView(task)}</Field>
          <Field label="Company">{task?.requestedByCompanyName ?? "—"}</Field>
          <Field label="Phone">{task?.requestedByPhone ?? "—"}</Field>
          {task?.comment && (
            <div className="col-span-2 md:col-span-3">
              <Field label="Note">{task.comment}</Field>
            </div>
          )}
        </section>

        {isMessageOnlySupport && (
          <Section title="Response comments">
            <div className="space-y-3">
              {(task.supportResponses ?? []).map((item, index) => (
                <div
                  key={`${item.respondedAt}-${index}`}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/40"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-blue_grey3 text-small">
                    <span className="font-semibold text-blue_grey">
                      {item.responderName ??
                        item.responderEmail ??
                        "PennEngineering Support"}
                    </span>
                    <span>{fmtDate(item.respondedAt)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-blue_grey text-body">
                    {item.response}
                  </p>
                </div>
              ))}

              {!isResponded ? (
                <>
                  <PennField
                    label="Response comments"
                    htmlFor="support-response-comments"
                  >
                    <PennTextarea
                      id="support-response-comments"
                      rows={5}
                      maxLength={5000}
                      value={supportResponse}
                      onChange={(event) =>
                        setSupportResponse(event.target.value)
                      }
                      placeholder="Enter the response discussed with the customer."
                    />
                  </PennField>
                  <div className="flex justify-end">
                    <PennButton
                      variant="primary"
                      size="sm"
                      onClick={handleRespondAndClose}
                      disabled={
                        respondAndClose.isPending ||
                        supportResponse.trim().length === 0
                      }
                    >
                      {respondAndClose.isPending
                        ? "Closing…"
                        : "Mark as Closed"}
                    </PennButton>
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-emerald-800 dark:text-emerald-200 text-body-medium">
                      This support task has been responded to and marked Quoted.
                    </span>
                    <div className="flex items-center gap-2">
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleSupportDownload("pdf")}
                        disabled={supportDocument.isPending}
                      >
                        Download response PDF
                      </PennButton>
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleSupportDownload("docx")}
                        disabled={supportDocument.isPending}
                      >
                        Download response Word
                      </PennButton>
                    </div>
                  </div>
                </div>
              )}

              {supportError && (
                <PennText as="p" size="xs" color="blue_grey">
                  {supportError}
                </PennText>
              )}
            </div>
          </Section>
        )}

        {/* Snapshot */}
        {!isMessageOnlySupport && (
          <Section title="Selection snapshot">
            {snapshot ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Field label="Solution">
                    {snapshot.solutionName ?? snapshot.solutionProductId ?? "—"}
                  </Field>
                  <Field label="Machine type">
                    {snapshot.solutionMachineType ?? "—"}
                  </Field>
                  <Field label="Region">{snapshot.filterRegion ?? "—"}</Field>
                  <Field label="Cost mode">
                    {snapshot.machineCostMode ?? "—"}
                  </Field>
                  <Field label="Investment">
                    {money(snapshot.investmentCost)}
                  </Field>
                  <Field label="Operator">{money(snapshot.operatorCost)}</Field>
                  <Field label="Quality">{money(snapshot.qualityCost)}</Field>
                  <Field label="Total">{money(snapshot.totalCost)}</Field>
                </div>
                {!snapshot.costAvailable && snapshot.costUnavailableReason && (
                  <PennText as="p" size="xs" color="blue_grey">
                    Cost unavailable: {snapshot.costUnavailableReason}
                  </PennText>
                )}
                {snapshot.selectedOptions.length > 0 && (
                  <div>
                    <PennText
                      as="p"
                      size="xs"
                      color="blue_grey"
                      className="uppercase mb-1"
                    >
                      Selected options ({snapshot.selectedOptions.length})
                    </PennText>
                    <ul className="space-y-1">
                      {snapshot.selectedOptions.map((o) => (
                        <li key={o.partNo} className="text-body">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-blue_grey">
                              {o.itemName ?? o.partNo}
                            </span>
                            <span className="text-blue_grey3">
                              {money(o.price)}
                            </span>
                          </div>
                          {o.requirements && (
                            <p className="text-blue_grey3 break-words text-small">
                              Requirements: {o.requirements}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <PennText as="p" size="sm" color="blue_grey">
                No snapshot on this task yet.
              </PennText>
            )}
          </Section>
        )}

        {/* Application attachments (read-only on the task; managed in the
            Applications editor). Surfaces the supporting files the requester
            uploaded on each application in this task. */}
        {!isMessageOnlySupport &&
          canConfigure &&
          (taskApplications?.length ?? 0) > 0 && (
            <Section title="Application attachments">
              <div className="space-y-4">
                {taskApplications!.map((app) => (
                  <div key={app.appId} className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <PennText
                        as="p"
                        size="sm"
                        font="semibold"
                        color="blue_grey1"
                      >
                        {app.name ?? app.applicationNumber ?? "Application"}
                        {app.applicationNumber && app.name
                          ? ` · ${app.applicationNumber}`
                          : ""}
                      </PennText>
                      {app.oem && (
                        <PennText as="span" size="xs" color="blue_grey">
                          OEM: {app.oem}
                        </PennText>
                      )}
                    </div>
                    {app.savedId ? (
                      <AttachmentsPanel
                        applicationId={app.savedId}
                        readOnly
                        compact
                      />
                    ) : (
                      <PennText as="p" size="xs" color="blue_grey">
                        Ad-hoc application (not saved to the library) — no
                        attachments.
                      </PennText>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

        {/* Manual line items */}
        {!isMessageOnlySupport && canGenerateQuote && (
          <Section title="Manual line items">
            {(lineItems?.length ?? 0) === 0 ? (
              <PennText as="p" size="sm" color="blue_grey">
                No manual line items.
              </PennText>
            ) : (
              <ul className="space-y-1 mb-3">
                {lineItems!.map((li) => (
                  <li
                    key={li.itemId}
                    className="flex items-center justify-between gap-3 text-body"
                  >
                    <span className="text-blue_grey">
                      {li.description ?? li.itemName ?? li.partNo}
                      {li.qty && li.qty > 1 ? ` × ${li.qty}` : ""}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-blue_grey3">{money(li.price)}</span>
                      <button
                        type="button"
                        className="text-red-600 hover:underline text-small"
                        onClick={() =>
                          deleteLine.mutate({ taskId, itemId: li.itemId })
                        }
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_0.6fr_auto] gap-2 items-end">
              <PennField label="Description">
                <PennInput
                  value={lineForm.description}
                  onChange={(e) =>
                    setLineForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="e.g. Custom tooling"
                />
              </PennField>
              <PennField label={`Price (${currency})`}>
                <PennInput
                  value={lineForm.price}
                  onChange={(e) =>
                    setLineForm((p) => ({ ...p, price: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </PennField>
              <PennField label="Qty">
                <PennInput
                  type="number"
                  value={lineForm.qty}
                  onChange={(e) =>
                    setLineForm((p) => ({ ...p, qty: e.target.value }))
                  }
                />
              </PennField>
              <PennButton
                variant="primary"
                size="sm"
                onClick={handleAddLine}
                disabled={addLine.isPending}
              >
                {addLine.isPending ? "Adding…" : "Add"}
              </PennButton>
            </div>
            {lineError && (
              <PennText as="p" size="xs" color="blue_grey" className="mt-1">
                {lineError}
              </PennText>
            )}
          </Section>
        )}

        {/* Approvals */}
        {!isMessageOnlySupport && canApprove && (
          <Section title="Approval workflow">
            {approval ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-small">
                  <Chip
                    text={
                      approval.rejected
                        ? "Rejected"
                        : approval.complete
                          ? "Approved / complete"
                          : current
                            ? `Pending: ${current.stepName}`
                            : "Not started"
                    }
                    tone={
                      approval.rejected
                        ? "red"
                        : approval.complete
                          ? "green"
                          : "amber"
                    }
                  />
                </div>

                {canDecide && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <PennButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleDecide("approve")}
                        disabled={decide.isPending}
                      >
                        Approve
                      </PennButton>
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDecide("reject")}
                        disabled={decide.isPending}
                      >
                        Reject
                      </PennButton>
                    </div>
                    <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                      <PennField label="Reassign to (user id)">
                        <PennInput
                          value={reassignTo}
                          onChange={(e) => setReassignTo(e.target.value)}
                          placeholder="user uuid"
                        />
                      </PennField>
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={handleReassign}
                        disabled={reassign.isPending}
                      >
                        Reassign
                      </PennButton>
                    </div>
                  </div>
                )}

                {approvalError && (
                  <PennText as="p" size="xs" color="blue_grey">
                    {approvalError}
                  </PennText>
                )}

                {approval.audit.length > 0 && (
                  <ul className="space-y-1">
                    {approval.audit.slice(0, 8).map((a) => (
                      <li key={a.id} className="text-blue_grey3 text-small">
                        {fmtDate(a.occurredAt)} · {a.eventType} · {a.actorEmail}
                        {a.decisionReason ? ` — ${a.decisionReason}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <PennText as="p" size="sm" color="blue_grey">
                No approval state.
              </PennText>
            )}
          </Section>
        )}

        {/* Documents */}
        {!isMessageOnlySupport && (
          <Section title="Documents">
            <div className="flex flex-wrap gap-2">
              {canGenerateRoi && (
                <>
                  <PennButton
                    variant="secondary"
                    size="sm"
                    onClick={handleCostAnalysis}
                    disabled={docBusy}
                  >
                    {costAnalysis.isPending
                      ? "Preparing…"
                      : "Cost Analysis (.xlsx)"}
                  </PennButton>
                  <PennButton
                    variant="secondary"
                    size="sm"
                    onClick={handleRoi}
                    disabled={docBusy}
                  >
                    {roiReport.isPending ? "Preparing…" : "ROI Report (.pdf)"}
                  </PennButton>
                </>
              )}
              {canGenerateQuote && (
                <>
                  <PennButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleRenderQuote("pdf")}
                    disabled={docBusy}
                  >
                    {downloadQuote.isPending ? "Generating…" : "Quotation (.pdf)"}
                  </PennButton>
                  <PennButton
                    variant="secondary"
                    size="sm"
                    onClick={() => handleRenderQuote("docx")}
                    disabled={docBusy}
                  >
                    Quotation (.docx)
                  </PennButton>
                </>
              )}
            </div>
            {docError && (
              <PennText as="p" size="xs" color="blue_grey" className="mt-2">
                {docError}
              </PennText>
            )}
          </Section>
        )}

        {/* Activity */}
        {(history?.items?.length ?? 0) > 0 && (
          <Section title="Activity">
            <ul className="space-y-1">
              {history!.items.slice(0, 10).map((h) => (
                <li key={h.id} className="text-blue_grey3 text-small">
                  {fmtDate(h.occurredAt)} · {h.eventType} · {h.toStatus} ·{" "}
                  {h.actorEmail}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </AdminModal>
  );
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// POC parity: "Requested by" shows the user NAME with the email underneath
// (work-for target first, else the submitter), never the company.
function requesterView(task: TaskDetail | undefined): React.ReactNode {
  if (!task) return "—";
  const name =
    [task.workingForFirstName, task.workingForLastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    [task.requestedByFirstName, task.requestedByLastName]
      .filter(Boolean)
      .join(" ")
      .trim();
  const email = task.workingForEmail ?? task.requestedByEmail ?? null;
  const primary = name || email || task.createdBy || "—";
  return (
    <div className="min-w-0">
      <div className="break-words">{primary}</div>
      {email && email !== primary && (
        <div className="break-words text-blue_grey3 text-small">
          {email}
        </div>
      )}
    </div>
  );
}

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <PennText
      as="p"
      size="xs"
      color="blue_grey"
      className="uppercase"
    >
      {label}
    </PennText>
    <div className="mt-1 text-blue_grey break-words text-body">
      {children}
    </div>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="border-t border-gray-100 dark:border-gray-800 pt-4">
    <PennText
      as="h3"
      size="sm"
      font="semibold"
      color="blue_grey1"
      className="mb-3"
    >
      {title}
    </PennText>
    {children}
  </section>
);

const Chip = ({
  text,
  tone,
}: {
  text: string;
  tone: "red" | "green" | "amber";
}) => {
  const cls =
    tone === "red"
      ? "bg-red-100 text-red-700 border-red-300"
      : tone === "green"
        ? "bg-green-100 text-green-700 border-green-300"
        : "bg-amber-100 text-amber-700 border-amber-300";
  return <span className={`px-2 py-0.5 rounded border ${cls}`}>{text}</span>;
};
